import { cacheKeys } from "@constants/cache-keys";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getAuthToken } from "@helpers/auth-provider";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { ItemData } from "types/item";
import { fetchDashboardItems } from "../api/queries";

const now = new Date();

const useItems = () => {
	const { userPublicMetadataId, isLoading: authLoading } = useAuthContext();

	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();

	const { data: dashboardData, isLoading } = useQuery({
		queryKey: [cacheKeys.dashboard],
		queryFn: async () => {
			if (!userPublicMetadataId) {
				return null;
			}
			const token = await getAuthToken();

			if (!token) {
				throw new Error("No token");
			}
			return await fetchDashboardItems({
				collectionId: studyPackCollectionId!,
			});
		},
		enabled: !!userPublicMetadataId,
	});

	const finalIsLoading = isLoading || authLoading;

	const items = useMemo(() => {
		return dashboardData?.data.items ?? [];
	}, [dashboardData]);

	const shuffleChildren = useCallback(
		(children: ItemData["data"]["children"]) => {
			return children.sort(() => Math.random() - 0.5);
		},
		[],
	);

	const filteredItems = useMemo(
		() =>
			items
				.filter((item) => {
					return (
						item.metadata?.generatedType === "VARIANT" &&
						// item.status === "PUBLISHED" &&
						item.publishedAt &&
						new Date(item.publishedAt) <= now
					);
				})
				.map((item) => ({
					...item,
					data: {
						...item.data,
						children: item.shuffle
							? shuffleChildren(item.data.children)
							: item.data.children,
					},
					media: item.media?.map((media) => ({
						...media,
						metadata: media.metadata
							? (JSON.parse(media.metadata as unknown as string) as {
									caption: string;
								})
							: undefined,
					})),
				})) || [],
		[items, shuffleChildren],
	);

	const futureItems = useMemo(() => {
		return dashboardData?.data.items?.filter(
			(item) => item.publishedAt && new Date(item.publishedAt) > now,
		);
	}, [dashboardData]);

	return {
		filteredItems,
		isLoading: finalIsLoading,
		futureItems,
	};
};

export default useItems;
